import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useSession } from '../../state/useSession';
import { returnTrip } from '../../shell/returnTo';
import { Button, Screen, TextField } from '../../ui';
import { holdPassword } from './heldPassword';
import type { ConfirmState } from './Confirm';

/** Cognito's name for an account whose email code was never entered. */
const NOT_CONFIRMED = 'UserNotConfirmedException';

export default function SignIn() {
  const { signIn } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  /** `confirmed`: sent by Confirm, which found the account already confirmed and nothing to enter. */
  const arrived = location.state as { from?: string; email?: string; confirmed?: boolean } | null;
  // Filled when Confirm, Sign up or a password reset sent them here: typed once is enough (F7).
  const [email, setEmail] = useState(arrived?.email ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  /** The account exists and its email was never confirmed: the way on is the code, not a retry. */
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  /** Where the person was sent from. Carried to sign-up and back, not only honoured here. */
  const requested = arrived?.from;

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setUnconfirmed(false);
    setBusy(true);
    try {
      await signIn(email.trim(), password);
      /*
       * Honoured only as a path on this site, and with an ad's idea handed over in state rather
       * than put back in the address, where the now-live pixel would send it to Meta. Both
       * rules live in shell/returnTo.ts.
       */
      const trip = returnTrip(requested);
      navigate(trip.to, { replace: true, ...(trip.state ? { state: trip.state } : {}) });
    } catch (err) {
      /*
       * Somebody who signed up, closed the tab before the emailed code and came back met
       * Cognito's "User is not confirmed." with nothing to press (F7, 2026-09-30). The pool only
       * says it for the right password, so that password is held for the sign-in the code
       * completes, and Confirm sends a fresh code on arrival.
       */
      if ((err as { code?: string } | null)?.code === NOT_CONFIRMED) {
        holdPassword(email, password);
        setUnconfirmed(true);
        return;
      }
      // The service's own message, not a generic one: "Incorrect username or password" and a
      // throttled attempt call for different actions from the person reading it.
      setError(err instanceof Error ? err.message : 'Could not sign you in.');
    } finally {
      setBusy(false);
    }
  }

  function confirm(): void {
    const state: ConfirmState = { email: email.trim(), resend: true, ...(requested ? { from: requested } : {}) };
    navigate('/confirm', { state });
  }

  return (
    <Screen title="Sign in">
      {arrived?.confirmed && (
        <p role="status" className="type-body">This account is already confirmed. Sign in with its password.</p>
      )}
      <form onSubmit={submit} noValidate>
        <TextField
          label="Email"
          type="email"
          autoComplete="email"
          value={email}
          onChangeText={setEmail}
          placeholder="you@example.com"
        />
        <TextField label="Password" type="password" autoComplete="current-password" value={password} onChangeText={setPassword} />
        {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
        {unconfirmed && (
          <div role="alert" className="type-caption field-error-text auth-notice">
            <p>This email isn&rsquo;t confirmed yet. We&rsquo;ll send you a new code to finish.</p>
            <Button title="Confirm your email" variant="secondary" onPress={confirm} />
          </div>
        )}
        <Button title="Sign in" type="submit" loading={busy} />
      </form>
      <p className="type-body auth-links">
        <Link to="/forgot-password" state={requested ? { from: requested } : undefined}>Forgot your password?</Link>
      </p>
      <p className="type-body auth-links">
        New here? <Link to="/sign-up" state={requested ? { from: requested } : undefined}>Create an account</Link>
      </p>
    </Screen>
  );
}
